import { ChevronRight } from "lucide-react";
import React, { useCallback } from "react";
import { Updater } from "use-immer";

import { IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { CustomEventFeType } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { eventTypes } from "@src/features/customEvents/helpers";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";

// Define retroactive event types as a constant
const RETROACTIVE_EVENT_TYPES = new Set([
  "collectionPageView",
  "productPageView",
  "pageView",
]);

interface CustomEventFeListProps {
  setSubpageSelected: (subpage: CustomEventFeType) => void;
  setDrawerPage: Updater<string>;
  onShowWarningDialog: (eventKey: CustomEventFeType) => void;
}

export const CustomEventFeList = ({
  setSubpageSelected,
  setDrawerPage,
  onShowWarningDialog,
}: CustomEventFeListProps) => {
  const theme = useTheme();
  // if no experience pretend status is pending to render all options (for event manager)
  const experience = useSelector(selectedExperience) ?? { status: "pending" };

  const canAddMetricPostLaunch =
    useConfiguration<boolean>("add_custom_metric_post_launch")?.value ?? false;

  // Helper function to flag events that can be filled retroactively when added
  const isRetroactiveAble = useCallback(
    (eventKey: string) => {
      return (
        experience?.status === "pending" ||
        RETROACTIVE_EVENT_TYPES.has(eventKey)
      );
    },
    [experience?.status]
  );

  // Helper function to determine if an event type is clickable
  const isEventClickable = useCallback(
    (eventKey: string) => {
      // Always clickable if canAddMetricPostLaunch is true
      if (canAddMetricPostLaunch) {
        return true;
      }

      // Otherwise, only clickable if experience is pending or if it's a page view type event
      return isRetroactiveAble(eventKey);
    },
    [canAddMetricPostLaunch, isRetroactiveAble]
  );

  // Handle event click with warning dialog for non-retroactive events
  const handleEventClick = useCallback(
    (eventKey: CustomEventFeType) => {
      if (isEventClickable(eventKey)) {
        if (!isRetroactiveAble(eventKey) && experience?.status !== "pending") {
          // For non-retroactive events that aren't in pending status, show warning dialog
          onShowWarningDialog(eventKey);
        } else {
          // Otherwise, proceed directly
          setSubpageSelected(eventKey);
          setDrawerPage("event");
        }
      }
    },
    [
      isEventClickable,
      isRetroactiveAble,
      experience?.status,
      onShowWarningDialog,
      setSubpageSelected,
      setDrawerPage,
    ]
  );

  return (
    <Stack direction={"column"} spacing={2} sx={{ width: "600px" }}>
      {eventTypes
        .filter((event) => event.key !== "javascriptImportedEvent")
        .map((event) => (
          <Grid key={event.key} sx={{ width: "100%" }} size={12}>
            <IgTooltip
              title={
                !isEventClickable(event.key)
                  ? "After a test has been started, only page view event metrics can be added."
                  : ""
              }
            >
              <Grid
                container
                direction={"row"}
                justifyContent={"space-between"}
                columnSpacing={4}
                rowSpacing={4}
                wrap={"nowrap"}
                alignItems={"center"}
                sx={{
                  "&:hover, &:focus": {
                    cursor: isEventClickable(event.key) ? "pointer" : "default",
                  },
                }}
                onClick={() => {
                  handleEventClick(event.key as CustomEventFeType);
                }}
              >
                <Grid
                  container
                  wrap={"nowrap"}
                  columnSpacing={4}
                  alignItems={"center"}
                >
                  <Grid sx={{ height: "35px" }}>
                    <event.icon sx={{ fontSize: "35px" }} />
                  </Grid>
                  <Stack direction={"column"}>
                    <IgTypography
                      sx={{
                        color: isEventClickable(event.key)
                          ? theme.palette.text.primary
                          : theme.palette.text.interactive.neutral.disabled,
                      }}
                    >
                      {event.title}
                    </IgTypography>

                    <IgTypography
                      variant={"body2"}
                      sx={{
                        color: isEventClickable(event.key)
                          ? theme.palette.text.secondary
                          : theme.palette.text.interactive.neutral.disabled,
                      }}
                    >
                      {event.description}
                    </IgTypography>
                  </Stack>
                </Grid>
                {isEventClickable(event.key) ? (
                  <Grid size={1}>
                    <ChevronRight size={18} />
                  </Grid>
                ) : null}
              </Grid>
            </IgTooltip>
          </Grid>
        ))}
    </Stack>
  );
};
